package com.iregained.site.components.sections.clinicians.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.clinicians.styles.CliniciansCtaButtonStyle
import com.iregained.site.components.widgets.SharedCtaActionLink
import com.iregained.site.components.widgets.SharedCtaWidget
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.modifiers.translateY
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.s
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Cinematic Rise
@Composable
private fun Modifier.cinematicRise(isVisible: Boolean): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else 50.px).transition(
        Transition.of("opacity", 0.8.s, TransitionTimingFunction.EaseOut),
        Transition.of("transform", 0.8.s, TransitionTimingFunction.EaseOut)
    )
}

@Composable
fun CliniciansCtaSection(data: CliniciansCtaData = defaultCliniciansCtaData) {
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "clinicians-cta-section"
    var isVisible by remember { mutableStateOf(false) }

    Div(attrs = Modifier.fillMaxWidth().toAttrs {
        ref { element ->
            val observer = IntersectionObserver { entries, obs ->
                entries.forEach {
                    if (it.isIntersecting) {
                        isVisible = true; obs.unobserve(element)
                    }
                }
            }
            observer.observe(element); onDispose { observer.disconnect() }
        }
    }) {
        Box(modifier = Modifier.fillMaxWidth().cinematicRise(isVisible)) {
            SharedCtaWidget(headline = data.headline, description = data.description) {
                if (data.buttonText.isNotBlank() || data.buttonPath.isNotBlank()) {
                    SharedCtaActionLink(
                        text = data.buttonText,
                        path = data.buttonPath,
                        styleModifier = CliniciansCtaButtonStyle.toModifier()
                    )
                }
            }
        }
    }
}

// ==========================================
// CMS DATA MODEL: CTA
// ==========================================
data class CliniciansCtaData(
    val sectionId: String? = null,
    val headline: String = "Elevate Your Clinic's Standard of Care.",
    val description: String = "Empower your therapists with precision robotics and objective data. Schedule a personalized walkthrough to see how the MyHand<sup>®</sup> System fits seamlessly into your workflow.",
    val buttonText: String = "Request a Clinical Demo",
    val buttonPath: String = "/contact#contact-b2b-demo-section" // Routes to your dynamic regional contact form
)

val defaultCliniciansCtaData = CliniciansCtaData()